import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Address,
  AddressInput,
  ChannelType,
  State,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  getExternalAddressText,
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import useSmartyStreets from 'features/signup//hooks/useSmartyStreets';
import { SmartyStreetsResponseItem } from 'features/signup/helpers/smartyStreets';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Keyboard, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetStates from 'services/graphql/queries/States';
import { addressSchema } from 'shared/components/vxl/input/schemas/address.schema';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useValidateAddress, {
  AddressView,
} from 'shared/hooks/useValidateAddress/useValidateAddress';
import { useQuery } from 'urql';

export const useTabaPayAddressUpdate = (screenType: string) => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const [showAddressFields, setShowAddressFields] = useState(false);
  const [isOutsideClicked, setIsOutsideClicked] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState<boolean>(false);
  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
    },
    resolver: zodResolver(addressSchema),
  });

  const [{ data: statesData }] = useQuery<{ states: State[] }>({
    query: GetStates,
    requestPolicy: 'cache-first',
  });

  const { addUserCardAddress, setUserCardDetailsAddress } = useTabaPayAddress();

  const [userEnteredAddress, setUserEnteredAddress] = useState<AddressInput>(
    {} as AddressInput,
  );

  const [cardAddress, setCardAddress] = useState<Address>();
  const [selectedAddress, setSelectedAddress] = useState('');
  const [goToNextScreen, setGoToNextScreen] = useState(false);
  const [isDialogButtonLoading, setIsDialogButtonLoading] = useState(false);
  const { loading, addressVerification } = useValidateAddress(
    userEnteredAddress as AddressInput,
    ChannelType.Signup,
  );

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const {
    tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount,
    isBelieveDay0Flow,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    isBelieveDay0Flow?: string;
  }>();

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType);
  const subcategory =
    isBelieveDay0Flow === 'true' ? 'dcf_believe_day_0_application' : flowName;

  const analyticsCommonAttributes = {
    subcategory,
    flow_name: flowName,
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'address-screen',
    customAttributes: analyticsCommonAttributes,
  });

  const enterAddressManuallyClicked = usePressableAnalytics({
    analyticsId: 'addressEnterManuallyClicked',
    customAttributes: analyticsCommonAttributes,
  });
  const manualAddressSelected = usePressableAnalytics({
    analyticsId: isManualAddress
      ? 'manualAddressSelected'
      : 'addressDropdownSelected',
    customAttributes: {
      address_selected: selectedAddress,
      ...analyticsCommonAttributes,
    },
  });

  const manualAddressConfirmed = usePressableAnalytics({
    analyticsId: 'manualAddressConfirmed',
    customAttributes: {
      address_selected: getExternalAddressText(cardAddress),
      ...analyticsCommonAttributes,
    },
  });

  const zipCodeErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'zip_code_validation',
      customer_message: errors.zip?.message,
      ...analyticsCommonAttributes,
    },
  });

  const streetAddressErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'street_address_validation',
      customer_message: errors.addressLine1?.message,
      ...analyticsCommonAttributes,
    },
  });

  const cityErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'city_validation',
      customer_message: errors.city?.message,
      ...analyticsCommonAttributes,
    },
  });

  useEffect(() => {
    if (errors.zip?.message) {
      zipCodeErrorEncountered();
    }
  }, [errors.zip?.message, zipCodeErrorEncountered]);

  useEffect(() => {
    if (errors.addressLine1?.message) {
      streetAddressErrorEncountered();
    }
  }, [errors.addressLine1?.message, streetAddressErrorEncountered]);

  useEffect(() => {
    if (errors.city?.message) {
      cityErrorEncountered();
    }
  }, [errors.city?.message, cityErrorEncountered]);

  useEffect(() => {
    if (selectedAddress) {
      manualAddressSelected();
    }
  }, [selectedAddress, manualAddressSelected]);

  useEffect(() => {
    if (isManualAddress && cardAddress) {
      manualAddressConfirmed();
    }
  }, [cardAddress, isManualAddress, manualAddressConfirmed]);

  useEffect(() => {
    if (
      addressVerification &&
      addressVerification?.addressType !== AddressView.FormAddress
    ) {
      Keyboard.dismiss();
      bottomSheetRef.current?.present();
    } else if (
      addressVerification !== undefined &&
      userEnteredAddress.addressLine1 !== null
    ) {
      const externalId =
        addressVerification.fullAddress?.originalAddress?.externalId;
      setCardAddress({ ...userEnteredAddress, externalId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressVerification]);

  useEffect(() => {
    if (goToNextScreen && addressVerification && cardAddress) {
      if (screenType === 'card-details') {
        setUserCardDetailsAddress(cardAddress);
        return;
      }
      if (screenType === 'tabapay') {
        addUserCardAddress(cardAddress);
        router.back();
      }
      setGoToNextScreen(false);
    }
  }, [
    addressVerification,
    cardAddress,
    goToNextScreen,
    addUserCardAddress,
    screenType,
    setUserCardDetailsAddress,
    router,
  ]);

  const updateAddress = (userEnteredAddress: AddressInput) => {
    if (userEnteredAddress && 'addressLine1' in userEnteredAddress) {
      setUserEnteredAddress({} as AddressInput);
      bottomSheetRef.current?.close();
      setIsDialogButtonLoading(false);
    }
  };

  const onSubmit = () => {
    setIsOutsideClicked(false);
    Keyboard.dismiss();
    const formData = getValues();
    const address = {
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: 'US',
    } as AddressInput;
    setUserEnteredAddress(address);
    setGoToNextScreen(true);
  };

  const [searchTerms, setSearchTerms] = useState('');
  const [selectedSearchTerms, setSelectedSearchTerms] =
    useState<SmartyStreetsResponseItem>();
  const { suggestions } = useSmartyStreets(searchTerms, selectedSearchTerms);

  const updateSuggestedAddress = () => {
    setIsDialogButtonLoading(true);
    const data = addressVerification?.fullAddress?.suggestedAddress;
    const address = {
      addressLine1: data?.addressLine1,
      addressLine2: data?.addressLine2,
      city: data?.city,
      state: data?.state,
      zip: data?.zip,
      country: 'US',
      externalId: data?.externalId,
    } as AddressInput;
    setCardAddress(address);
    setUserEnteredAddress(address);
    void updateAddress(address);
  };

  const showAddressField = () => {
    setIsManualAddress(true);
    setShowAddressFields(true);
    if (screenType === 'tabapay' || screenType === 'card-details') {
      enterAddressManuallyClicked();
    }
  };

  const { height } = useWindowDimensions();

  const handleBackButtonAction = useCallback(() => {
    navigateBack();
    return true;
  }, []);

  useBackHandler(handleBackButtonAction);

  const onSubmitEditingAction = () => {
    if (isValid) {
      onSubmit();
    }
  };

  return {
    onSubmitEditingAction,
    height,
    showAddressField,
    updateSuggestedAddress,
    suggestions,
    setSelectedSearchTerms,
    setSearchTerms,
    loading,
    isDialogButtonLoading,
    setSelectedAddress,
    statesData,
    bottom,
    control,
    reset,
    showAddressFields,
    isOutsideClicked,
    setIsOutsideClicked,
    selectedSearchTerms,
    errors,
    setShowAddressFields,
    onSubmit,
    isValid,
    bottomSheetRef,
    addressVerification,
    tabaPayEntryFlowType,
  };
};
